import { useCallback } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { CCard, CCardBody, CCol } from '@coreui/react';
import FeedbackForm from 'components/contact/FeedbackForm';
import useSendFeedback from 'hooks/contact/useSendFeedback';
import useGetMessage from 'hooks/contact/useGetMessage';
import Loading from 'components/common/Loading';
import { toast } from 'react-toastify';
import { FormattedMessage, useIntl } from 'react-intl';
import type { FormikHelpers } from 'formik';
import type { IFeedbackFormValues } from 'components/contact/FeedbackForm';
import type { IMessageRow } from 'components/contact/MessagesTable';
import { messageOf } from 'utils/error';
import emailHtmlOf from 'components/common/Editor.utils';
import { routePatterns } from 'router/route-paths';

/** The location state Messages navigates here with. */
interface IFeedbackLocationState {
  messageDetails?: IMessageRow & { page: number };
}

/** What the page shows of the message it answers. */
interface IFeedbackMessage {
  id: string;
  name: string;
  email: string;
  fullMessage: string;
  /** The page of the messages table to refetch after the answer. */
  page: number;
}

const useSendFeedbackHandler = ({ messageId, page }: { messageId: string; page: number }) => {
  const sendFeedback = useSendFeedback();
  const { formatMessage: t } = useIntl();

  const handleSend = useCallback(
    async (
      vars: IFeedbackFormValues,
      { setSubmitting, resetForm }: FormikHelpers<IFeedbackFormValues>,
    ) => {
      try {
        setSubmitting(true);
        const sent = await sendFeedback(
          { ...vars, message: emailHtmlOf(vars.message), messageId },
          page,
        );
        if (sent === false) toast.info(t({ id: 'messages.saved_without_email' }));
        else toast.success(t({ id: 'messages.message_sent' }));
        resetForm();
      } catch (error) {
        toast.error(messageOf(error));
      } finally {
        setSubmitting(false);
      }
    },
    [messageId, page, sendFeedback, t],
  );

  return handleSend;
};

const FeedbackOfMessage = ({ message }: { message: IFeedbackMessage }) => {
  const { id, name, email, fullMessage, page } = message;
  const initialValues = { name, email, message: '' };
  const onSubmit = useSendFeedbackHandler({ messageId: id, page });

  return <FeedbackForm {...{ initialValues, message: fullMessage, onSubmit }} />;
};

const MessageNotFound = () => (
  <CCol xs={12} md={9} lg={7} className="mx-auto">
    <CCard>
      <CCardBody>
        <p>
          <FormattedMessage id="messages.not_found" />
        </p>
        <Link to={routePatterns.messages.stringify({ page: 1 })} className="link">
          <FormattedMessage id="messages.back_to_messages" />
        </Link>
      </CCardBody>
    </CCard>
  </CCol>
);

/**
 * The answer form of a message: opened from the messages table it shows the row's message (the location state),
 * opened directly or reloaded it loads the message by the id in the path.
 */
const Feedback = () => {
  const location = useLocation<IFeedbackLocationState | undefined>();
  const { id } = useParams<{ id: string }>();
  const details = location.state?.messageDetails;
  const { message, loading } = useGetMessage({ messageId: id, skip: Boolean(details) });

  if (details) {
    const { id: messageId, name, email, fullMessage, page } = details;
    return <FeedbackOfMessage message={{ id: messageId, name, email, fullMessage, page }} />;
  }
  if (loading) return <Loading size={30} />;
  if (!message) return <MessageNotFound />;

  return (
    <FeedbackOfMessage
      message={{
        id: message._id,
        name: message.sender,
        email: message.email,
        fullMessage: message.message,
        page: 1,
      }}
    />
  );
};

export default Feedback;
